import { useEffect } from 'react'
import { useLingui } from '@lingui/react/macro'
import { CircleLoadingAnimation } from '@vgw/multibrand-fe-circle-loading-animation'
import { useToast } from '@vgw/multibrand-fe-toast'
import { usePurchaseLimitsQuery } from '@/features/purchase-limits/use-purchase-limits-query/use-purchase-limits-query'
import { PurchaseLimitForm } from '@/features/purchase-limits/purchase-limit-form/purchase-limit-form'
import { PurchaseLimitFormValues } from '@/features/purchase-limits/purchase-limit-form/purchase-limit-form-schema'
import { useAddPurchaseLimitMutation } from '@/features/purchase-limits/use-add-purchase-limit-mutation'
import { toPurchaseLimitPayload } from '@/features/purchase-limits/purchase-limits-utils'
import { useTracking } from '@vgw/multibrand-fe-tracking'
import { PURCHASE_LIMITS_TITLES } from '@/features/purchase-limits/constants'
import { useRouter, createFileRoute } from '@tanstack/react-router'
import { TertiaryPageLayout } from '@/routes/-tertiary-page-layout'
import { GameLimitsInfo } from '@/features/purchase-limits/game-limits-info'

export const Route = createFileRoute('/_app/your-play/gameplay-limits/add')({
  component: AddPurchaseLimit,
})

function AddPurchaseLimit() {
  const { track, setUserTrackingProperty } = useTracking()
  const router = useRouter()
  const { limits, isLoading, error } = usePurchaseLimitsQuery()
  const { show } = useToast()
  const { t } = useLingui()

  useEffect(() => {
    if (error) {
      show({
        description: t`Something went wrong.`,
        variant: 'warning',
        close: false,
      })
    }
  }, [error, show, t])

  const { mutateAsync: addPurchaseLimit } = useAddPurchaseLimitMutation()

  const handleConfirmedSubmit = async (values: PurchaseLimitFormValues) => {
    const purchaseLimitPayload = toPurchaseLimitPayload(values)

    await addPurchaseLimit(
      {
        path: { limitType: purchaseLimitPayload.limitType },
        body: {
          limitValue: purchaseLimitPayload.limitValue,
        },
      },
      {
        onSuccess: () => {
          router.history.back()

          setUserTrackingProperty('has purchase limit', true)
          track('Purchase Limit Applied', {
            eventData: {
              'purchase limit type': t(
                PURCHASE_LIMITS_TITLES[purchaseLimitPayload.limitType],
              ),
              'purchase limit amount': purchaseLimitPayload.limitValue / 100,
            },
          })
        },
      },
    ).catch((error) => {
      show({
        description:
          error?.detail || t`Something went wrong. Please try again.`,
        variant: 'warning',
        close: false,
      })
    })
  }

  return (
    <TertiaryPageLayout
      infoDrawerContent={<GameLimitsInfo />}
      headerText={t`Purchase Limit`}
    >
      {isLoading ? (
        <div data-testid="add-purchase-limit-spinner">
          <CircleLoadingAnimation className="mx-auto mt-[60px]" />
        </div>
      ) : (
        <PurchaseLimitForm
          limits={limits}
          onConfirmedSubmit={handleConfirmedSubmit}
        />
      )}
    </TertiaryPageLayout>
  )
}
